import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  concatMap,
  EMPTY,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  pipe,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { type ZipProgressEvent } from '@shared/types';

import {
  OUTCOME_READY_DEFAULT_SIGNAL_STATE,
  OUTCOME_READY_GROUP_PHASE_RESET_MS,
  OUTCOME_READY_SNACKBAR_CLASS,
  OUTCOME_READY_TOAST,
  OUTCOME_READY_ZIP_THRESHOLD,
} from '@data-access/dash-board/constants';

import {
  OutcomeDocument,
  OutcomeDownloadEvent,
  OutcomeDownloadOutcome,
  OutcomeDownloadResult,
  OutcomeGroupPhase,
  OutcomeReadyRequestParams,
  OutcomeReadySignalStateType,
  OutcomeReadySubmission,
  OutcomeStandaloneDocument,
} from '../../types';
import { DashboardService } from '../../services';
import {
  classifyDownloadFailure,
  isAccessFailure,
  isRevokedAccessStatus,
  isRevokedFailure,
  toDownloadPercent,
} from '../../utils';

/**
 * Module-level signal state backing the dashboard "Outcomes ready" card.
 * Shared so the card, its resolver, and its rows all read the same slice.
 */
export const OUTCOME_READY_SIGNAL_STATE =
  signalState<OutcomeReadySignalStateType>({
    ...OUTCOME_READY_DEFAULT_SIGNAL_STATE,
  });

/**
 * State facade for the dashboard "Outcomes ready" card.
 *
 * Owns loading of the Outcomes Ready payload, single-accordion expansion, and
 * single/group document downloads with their toast messaging.
 *
 * @example
 * ```ts
 * private readonly _outcomesReady = inject(OutcomeReadySignalState);
 * readonly $groups = this._outcomesReady.$submissions;
 * ```
 */
@Injectable()
export class OutcomeReadySignalState {
  private readonly _dashboardService = inject(DashboardService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _outcomeReadySignalState = OUTCOME_READY_SIGNAL_STATE;

  /**
   * Pending phase-reset timers keyed by workspace id.
   *
   * Tracked so a repeat download of the same group restarts its 2s dwell
   * instead of letting the previous timer clear the new indicator early, and so
   * every timer can be canceled when the state is torn down.
   */
  private readonly _phaseTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  /** The raw Outcomes Ready payload, or null before the first successful load. */
  readonly $data = computed(() => this._outcomeReadySignalState.data());

  /** True while the Outcomes Ready request is in-flight. */
  readonly $isLoading = computed(() =>
    this._outcomeReadySignalState.isLoading(),
  );

  /** Populated when the Outcomes Ready request fails; null otherwise. */
  readonly $error = computed(() => this._outcomeReadySignalState.error());

  /** Workspace id of the single expanded group; null when every group is collapsed. */
  readonly $expandedWorkspaceId = computed(() =>
    this._outcomeReadySignalState.expandedWorkspaceId(),
  );

  /** Document ids whose own transfer is in flight, for per-row indicators. */
  readonly $downloadingIds = computed(() =>
    this._outcomeReadySignalState.downloadingIds(),
  );

  /**
   * Document ids owned by an in-flight zipped group download.
   *
   * Busy, but with no individually knowable state — rows render disabled and
   * show no indicator of their own.
   */
  readonly $queuedIds = computed(() =>
    this._outcomeReadySignalState.queuedIds(),
  );

  /**
   * Transfer percentage per in-flight document id.
   *
   * A downloading id missing from this map has an unknown total (no readable
   * `Content-Length`) and must render an indeterminate indicator.
   */
  readonly $downloadProgress = computed(() =>
    this._outcomeReadySignalState.downloadProgress(),
  );

  /** Document-count percentage per in-flight group, keyed by workspace id. */
  readonly $groupProgress = computed(() =>
    this._outcomeReadySignalState.groupProgress(),
  );

  /** Transient success/partial/error phase per submission workspace id. */
  readonly $groupPhases = computed(() =>
    this._outcomeReadySignalState.groupPhases(),
  );

  /** Submission groups returned by the API; empty before the first load. */
  readonly $submissions = computed(() => this.$data()?.submissions ?? []);

  /** Outcome documents with no owning submission; empty before the first load. */
  readonly $standaloneDocuments = computed(
    () => this.$data()?.standalone_documents ?? [],
  );

  /** Total number of outcome documents across groups and standalone rows. */
  readonly $documentCount = computed(() => this.$data()?.document_count ?? 0);

  /** Total number of submission groups returned by the API. */
  readonly $submissionCount = computed(
    () => this.$data()?.submission_count ?? 0,
  );

  /** Card header summary, e.g. `10 docs · 3 submissions`. */
  readonly $summaryLabel = computed(
    () =>
      `${this.$documentCount()} docs · ${this.$submissionCount()} submissions`,
  );

  /** True when the card has at least one group or standalone document to render. */
  readonly $hasOutcomes = computed(
    () =>
      this.$submissions().length > 0 || this.$standaloneDocuments().length > 0,
  );

  /** True when at least one standalone document row must be rendered. */
  readonly $hasStandaloneDocuments = computed(
    () => this.$standaloneDocuments().length > 0,
  );

  /**
   * Loads the Outcomes Ready payload and reflects the request lifecycle into state.
   * Re-invoking it cancels any in-flight request.
   *
   * @param options - Optional fetch behaviour. `useMock` is forwarded to
   * `DashboardService.getOutcomesReady` — when true the service serves its
   * typed mock payload instead of calling the API, driven by the card
   * component's `mockToggle` input. `silent` skips raising `isLoading`, so a
   * post-download refetch swaps the data in place instead of flashing the
   * whole card back to its loading skeleton. Omit for a plain fetch.
   */
  readonly getOutcomesReady = rxMethod<{
    requestQueryParams?: OutcomeReadyRequestParams;
    useMock?: boolean;
    silent?: boolean;
  }>(
    pipe(
      tap((options) =>
        patchState(this._outcomeReadySignalState, {
          isLoading: !options?.silent,
          error: null,
        }),
      ),
      switchMap(({ requestQueryParams, useMock }) =>
        this._dashboardService
          .getOutcomesReady(requestQueryParams, useMock === true)
          .pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(this._outcomeReadySignalState, {
                  error: error,
                  isLoading: false,
                }),
              next: (response) => {
                patchState(this._outcomeReadySignalState, {
                  error: null,
                  isLoading: false,
                  data: response,
                });
              },
            }),
          ),
      ),
    ),
  );

  /**
   * Downloads a single outcome document, saves it, and raises the matching toast.
   *
   * When the document is no longer accessible (401/403/404) the payload is
   * re-fetched so the revoked row disappears and the counts adjust.
   *
   * Implemented as an `rxMethod` so the subscription is owned by this service's
   * injector: the state is provided at route scope, and a bare `.subscribe()`
   * would outlive navigation away from the dashboard — saving a file and firing
   * a toast on a page the user already left. The leading `filter` makes repeated
   * activations of the same row a no-op while its download is still in flight.
   *
   * `mergeMap` (not `switchMap`) is deliberate: downloading a second document
   * must not cancel the first one mid-flight and silently lose a requested file.
   *
   * @param doc - The document row the user activated.
   */
  readonly downloadDocument = rxMethod<
    OutcomeDocument | OutcomeStandaloneDocument
  >(
    pipe(
      filter((doc) => !this._isBusy(doc.id)),
      tap((doc) => this._addDownloadingIds([doc.id])),
      mergeMap((doc) =>
        this._dashboardService.downloadOutcomeDocumentWithProgress(doc.id).pipe(
          tapResponse({
            next: (event: OutcomeDownloadEvent) => {
              if (event.kind === 'progress') {
                this._setProgress(doc.id, event.percent);
                return;
              }
              this._handleDocumentResult(doc, event.outcome);
            },
            error: () => this._showGeneralFailure(),
            finalize: () => this._removeDownloadingIds([doc.id]),
          }),
        ),
      ),
    ),
  );

  /**
   * Downloads every document of a submission group, saves each successful one,
   * and raises a single summary toast for the whole group.
   *
   * Uses `rxMethod` for the same lifecycle reason as
   * {@link OutcomeReadySignalState.downloadDocument}. The leading `filter`
   * drops empty groups and ignores a repeat activation while the group's
   * documents are still downloading or queued.
   *
   * Small groups download their files concurrently; groups above
   * {@link OUTCOME_READY_ZIP_THRESHOLD} are handed to the serialized zip queue
   * instead — see {@link OutcomeReadySignalState._drainZipQueue} for why the
   * archive path must never run two jobs at once.
   *
   * @param submission - The submission group the user activated.
   */
  readonly downloadSubmissionGroup = rxMethod<OutcomeReadySubmission>(
    pipe(
      filter((submission) => {
        const _ids = this._documentIds(submission);
        return _ids.length > 0 && !_ids.some((id) => this._isBusy(id));
      }),
      tap((submission) =>
        this._setGroupPhase(submission.workspace_id, 'downloading'),
      ),
      mergeMap((submission) => {
        if (submission.documents.length > OUTCOME_READY_ZIP_THRESHOLD) {
          this._enqueueGroupZip(submission);
          return EMPTY;
        }
        return this._downloadGroupIndividually(submission);
      }),
    ),
  );

  /**
   * Serialized zip pipeline: one archive job at a time, in activation order.
   *
   * The platform zip worker is a **single shared instance** whose `onmessage`
   * handler is reassigned on every `zipDocuments` call. Two in-flight archive
   * jobs therefore clobber each other's completion handler: both blobs are
   * saved through whichever closure was registered last, and every archive
   * ends up under one submission's file name while the other names are lost
   * (ERM-13518). `concatMap` holds each job — and with it the submission
   * closure carrying the archive's name — until its own `done` event, so
   * concurrent activations are queued rather than raced and no group's name
   * is ever dropped.
   */
  private readonly _drainZipQueue = rxMethod<OutcomeReadySubmission>(
    pipe(concatMap((submission) => this._downloadGroupAsZip(submission))),
  );

  /** Cancels any pending group-phase timers when the owning injector is destroyed. */
  constructor() {
    this._destroyRef.onDestroy(() => this._clearAllPhaseTimers());
  }

  /**
   * @NOTE: PUBLIC METHODS
   */

  /**
   * Expands the given submission group, or collapses it when it is already the
   * expanded one. Enforces one-accordion-open-at-a-time.
   *
   * @param workspaceId - Workspace id of the group the user activated.
   */
  toggleSubmission(workspaceId: string): void {
    patchState(this._outcomeReadySignalState, (state) => ({
      expandedWorkspaceId:
        state.expandedWorkspaceId === workspaceId ? null : workspaceId,
    }));
  }

  /** Restores the card to its pristine state and cancels any pending indicators. */
  resetState(): void {
    this._clearAllPhaseTimers();
    patchState(this._outcomeReadySignalState, {
      ...OUTCOME_READY_DEFAULT_SIGNAL_STATE,
      downloadProgress: {},
      groupProgress: {},
      groupPhases: {},
    });
  }

  loadErrorState() {
    patchState(this._outcomeReadySignalState, {
      isLoading: false,
      error: new Error('Error State Loading'),
    });
  }

  loadEmptyState() {
    patchState(this._outcomeReadySignalState, {
      isLoading: false,
      error: null,
      data: null,
    });
  }

  loadLoadingState() {
    patchState(this._outcomeReadySignalState, {
      isLoading: true,
      error: null,
      data: null,
    });
  }

  /**
   * @NOTE: PRIVATE METHODS
   */

  /**
   * Downloads a small group as individual files, one save per document.
   *
   * Used at or below {@link OUTCOME_READY_ZIP_THRESHOLD}, where zipping would
   * make the user unpack an archive to reach one or two documents.
   *
   * Every document here has its own request and therefore its own measured
   * byte progress, so each row is driven entirely by its own transfer: it is
   * marked downloading, reports its own percentage, and is released the moment
   * *it* settles rather than when the slowest member of the group does.
   *
   * @param submission - The submission group being downloaded.
   * @returns Observable that completes once every document has settled.
   */
  private _downloadGroupIndividually(
    submission: OutcomeReadySubmission,
  ): Observable<unknown> {
    const _ids = this._documentIds(submission);
    this._addDownloadingIds(_ids);

    return forkJoin(
      submission.documents.map((doc) =>
        this._dashboardService.downloadOutcomeDocumentWithProgress(doc.id).pipe(
          tap((event) => {
            if (event.kind === 'progress') {
              this._setProgress(doc.id, event.percent);
            }
          }),
          filter(
            (
              event,
            ): event is Extract<OutcomeDownloadEvent, { kind: 'complete' }> =>
              event.kind === 'complete',
          ),
          take(1),
          // Releases this row alone. `forkJoin` withholds its own emission
          // until every source completes, so clearing only in the outer
          // `finalize` would hold a finished document's indicator open for as
          // long as its slowest sibling — the rows would appear to settle in
          // lockstep even though their transfers did not.
          tap(() => this._removeDownloadingIds([doc.id])),
          map(
            (event): OutcomeDownloadResult => ({
              doc,
              outcome: event.outcome,
            }),
          ),
        ),
      ),
    ).pipe(
      tapResponse({
        next: (results: OutcomeDownloadResult[]) =>
          this._handleGroupResults(submission, results),
        error: () => this._failGroup(submission),
        // Safety net only — each document already released itself above. This
        // catches the paths that never reach a per-document completion: an
        // error, or teardown before the group settles.
        finalize: () => this._removeDownloadingIds(_ids),
      }),
    );
  }

  /**
   * Marks a group's rows busy and hands its archive job to the serialized
   * zip queue.
   *
   * Rows are marked *queued* here — at activation, not when the job reaches
   * the front of the queue — so the repeat-activation guard in
   * {@link OutcomeReadySignalState.downloadSubmissionGroup} sees them as busy
   * for the whole wait and a second click cannot enqueue the same group twice.
   *
   * @param submission - The submission group to archive.
   */
  private _enqueueGroupZip(submission: OutcomeReadySubmission): void {
    this._addQueuedIds(this._documentIds(submission));
    this._drainZipQueue(submission);
  }

  /**
   * Downloads a whole group as a single zip archive.
   *
   * Runs only from {@link OutcomeReadySignalState._drainZipQueue}, one job at
   * a time — the shared zip worker cannot host two jobs concurrently. The
   * group's rows were already marked queued at activation by
   * {@link OutcomeReadySignalState._enqueueGroupZip}; this method releases
   * them when the job settles.
   *
   * Used above {@link OUTCOME_READY_ZIP_THRESHOLD}. Delegating to the platform
   * zip worker is what makes the AC's "standard download behavior of documents
   * in the system" true, and it inherits the worker's retries, concurrency
   * limiting, duplicate-name handling, and `Failed Downloads.txt` manifest.
   *
   * Progress is **document-count based** and belongs to the group, not to any
   * document in it. The worker reports how many documents have settled but
   * never which ones, and its transfers expose no readable `Content-Length`
   * cross-origin, so no per-document figure exists on this path at all.
   *
   * The group's members are therefore marked *queued* rather than downloading:
   * busy, with no indicator of their own. Writing the group's percentage under
   * each member's document id instead — which is what this used to do — gave
   * every row in the group an identical ring that advanced and retreated in
   * lockstep, because it was the same number rendered five times.
   *
   * @param submission - The submission group being downloaded.
   * @returns Observable that completes once the archive has been produced.
   */
  private _downloadGroupAsZip(
    submission: OutcomeReadySubmission,
  ): Observable<unknown> {
    const _ids = this._documentIds(submission);
    const _workspaceId = submission.workspace_id;

    return this._dashboardService.downloadOutcomeGroupAsZip(submission).pipe(
      tapResponse({
        next: (event: ZipProgressEvent) => {
          if (event.type === 'progress') {
            this._setGroupProgress(_workspaceId, event.completed, event.total);
            return;
          }
          this._handleZipResult(submission, event);
        },
        error: () => this._failGroup(submission),
        finalize: () => {
          this._removeQueuedIds(_ids);
          this._clearGroupProgress(_workspaceId);
        },
      }),
    );
  }

  /**
   * Resolves a single-document download emission into a save plus the matching
   * toast, re-fetching the payload when the document is no longer reachable.
   *
   * @param doc - The document the download was triggered for.
   * @param response - Raw emission from `downloadOutcomeDocument`.
   */
  private _handleDocumentResult(
    doc: OutcomeDocument | OutcomeStandaloneDocument,
    outcome: OutcomeDownloadOutcome,
  ): void {
    if (outcome.status === 'success') {
      this._dashboardService.saveOutcomeDocument(outcome.response, doc.title);
      this._showToast(
        OUTCOME_READY_TOAST.documentSuccess(doc.title),
        OUTCOME_READY_SNACKBAR_CLASS.success,
      );
      return;
    }

    if (outcome.reason === 'access') {
      this._showToast(
        OUTCOME_READY_TOAST.noLongerAvailable,
        OUTCOME_READY_SNACKBAR_CLASS.error,
      );
      this.getOutcomesReady({
        silent: true,
      });
      return;
    }

    this._showGeneralFailure();
  }

  /**
   * Saves the successful downloads of a group and raises the single summary toast.
   *
   * @param submission - The submission group the download was triggered from.
   * @param results - Per-document download outcomes.
   */
  private _handleGroupResults(
    submission: OutcomeReadySubmission,
    results: OutcomeDownloadResult[],
  ): void {
    const _succeeded = results.filter(
      (result) => result.outcome.status === 'success',
    );
    const _failed = results.filter(
      (result) => result.outcome.status === 'failure',
    );

    for (const _result of _succeeded) {
      if (_result.outcome.status === 'success') {
        this._dashboardService.saveOutcomeDocument(
          _result.outcome.response,
          _result.doc.title,
        );
      }
    }

    if (!_failed.length) {
      this._showToast(
        OUTCOME_READY_TOAST.groupSuccess(
          _succeeded.length,
          submission.workspace_name,
        ),
        OUTCOME_READY_SNACKBAR_CLASS.success,
      );
      this._setGroupPhase(submission.workspace_id, 'success');
      return;
    }

    const _hasAccessFailure = _failed.some((result) =>
      isAccessFailure(result.outcome),
    );
    /*
     * As in `_handleZipResult`: copy keys on revoked access, refetch keys on
     * the broader `'access'` class. A count rather than a boolean, because the
     * mixed case names how many documents are gone.
     */
    const _revokedCount = _failed.filter((result) =>
      isRevokedFailure(result.outcome),
    ).length;

    if (_succeeded.length) {
      this._showToast(
        OUTCOME_READY_TOAST.partial(_failed.length),
        OUTCOME_READY_SNACKBAR_CLASS.warning,
      );
      this._setGroupPhase(submission.workspace_id, 'partial');
    } else {
      this._showToast(
        this._resolveAllFailedToast(submission, _revokedCount, _failed.length),
        OUTCOME_READY_SNACKBAR_CLASS.error,
      );
      this._setGroupPhase(submission.workspace_id, 'error');
    }

    if (_hasAccessFailure) {
      this.getOutcomesReady({ silent: true });
    }
  }

  /**
   * Resolves a completed zip download into the matching toast and group phase.
   *
   * @param submission - The submission group the archive was built for.
   * @param event - The worker's completion event.
   */
  private _handleZipResult(
    submission: OutcomeReadySubmission,
    event: Extract<ZipProgressEvent, { type: 'done' }>,
  ): void {
    const _hasAccessFailure = event.failures.some(
      (failure) => classifyDownloadFailure(failure.status) === 'access',
    );
    /*
     * Copy keys on *revoked* access only, which excludes 401 — see
     * `isRevokedAccessStatus`. The refetch above still keys on the broader
     * `'access'` class, because any of the three means this row may be stale.
     */
    const _revokedCount = event.failures.filter((failure) =>
      isRevokedAccessStatus(failure.status),
    ).length;

    if (event.hasAllFailed || !event.savedArchive) {
      this._showToast(
        this._resolveAllFailedToast(
          submission,
          _revokedCount,
          event.failures.length,
        ),
        OUTCOME_READY_SNACKBAR_CLASS.error,
      );
      this._setGroupPhase(submission.workspace_id, 'error');
    } else if (event.failedCount > 0) {
      this._showToast(
        OUTCOME_READY_TOAST.partial(event.failedCount),
        OUTCOME_READY_SNACKBAR_CLASS.warning,
      );
      this._setGroupPhase(submission.workspace_id, 'partial');
    } else {
      this._showToast(
        OUTCOME_READY_TOAST.groupSuccess(
          event.successCount,
          submission.workspace_name,
        ),
        OUTCOME_READY_SNACKBAR_CLASS.success,
      );
      this._setGroupPhase(submission.workspace_id, 'success');
    }

    // Same contract as the per-document path: only a revoked-access failure
    // re-fetches, so a transient 5xx never silently removes a row.
    if (_hasAccessFailure) {
      this.getOutcomesReady({ silent: true });
    }
  }
  /**
   * Applies the whole-group failure treatment: named toast plus error phase.
   *
   * @param submission - The submission group that failed outright.
   */
  private _failGroup(submission: OutcomeReadySubmission): void {
    this._showToast(
      OUTCOME_READY_TOAST.groupFailure(submission.workspace_name),
      OUTCOME_READY_SNACKBAR_CLASS.error,
    );
    this._setGroupPhase(submission.workspace_id, 'error');
  }

  /**
   * Records a group's document-count percentage against its workspace id.
   *
   * The zip worker reports "n of N documents settled" rather than bytes. That
   * is a fact about the group and is stored as one, under one key. An earlier
   * revision spread it across every member's document id so the rows could
   * render it, which is exactly why every row in a group showed the same
   * moving figure.
   *
   * @param workspaceId - Workspace id of the group being downloaded.
   * @param completed - Documents settled so far.
   * @param total - Documents in the archive.
   */
  private _setGroupProgress(
    workspaceId: string,
    completed: number,
    total: number,
  ): void {
    const _percent = toDownloadPercent(completed, total);

    patchState(this._outcomeReadySignalState, (state) => {
      const _next = { ...state.groupProgress };

      if (_percent === null) {
        delete _next[workspaceId];
      } else {
        _next[workspaceId] = _percent;
      }

      return { groupProgress: _next };
    });
  }

  /**
   * Drops a settled group's progress entry.
   *
   * @param workspaceId - Workspace id whose progress should be cleared.
   */
  private _clearGroupProgress(workspaceId: string): void {
    patchState(this._outcomeReadySignalState, (state) => {
      const _next = { ...state.groupProgress };
      delete _next[workspaceId];
      return { groupProgress: _next };
    });
  }

  /**
   * Collects the document ids belonging to a submission group.
   *
   * @param submission - The submission group to read ids from.
   * @returns The group's document ids; empty when the group carries none.
   */
  private _documentIds(submission: OutcomeReadySubmission): string[] {
    return (submission.documents ?? []).map((doc) => doc.id);
  }

  /**
   * Reports whether a document is already tied up by any download.
   *
   * Covers both a document transferring on its own and one belonging to an
   * in-flight zipped group, so a repeat activation cannot fan out a duplicate
   * request for a document the archive is already fetching.
   *
   * @param id - Document id to test.
   * @returns True when the id is currently downloading or queued.
   */
  private _isBusy(id: string): boolean {
    return (
      this._outcomeReadySignalState.downloadingIds().includes(id) ||
      this._outcomeReadySignalState.queuedIds().includes(id)
    );
  }

  /**
   * Raises a download toast through the dashboard data service.
   *
   * @param message - Approved product copy from `OUTCOME_READY_TOAST`.
   * @param panelClass - Panel class from `OUTCOME_READY_SNACKBAR_CLASS`.
   */
  private _showToast(message: string, panelClass: string): void {
    this._dashboardService.notifyOutcomeDownload(message, panelClass);
  }

  /**
   * Picks the copy for a group download where nothing was saved.
   *
   * ERM-13269: reason before count. The branch used to key on "did everything
   * fail", never on why, so a revoked-permission group read identically to a
   * network blip — including the "Please try again" that a revoked group can
   * never act on. The three shapes are now distinguished:
   *
   * - every failure revoked — say so, and drop the retry invitation
   * - some revoked, some not — name how many are gone, but still invite the
   *   retry, because the transient ones may well succeed
   * - none revoked — the plain retryable copy
   *
   * Shared by both group handlers so the zipped and file-by-file paths cannot
   * drift apart, which is how they came to disagree in the first place.
   *
   * @param submission - The submission group that failed outright.
   * @param revokedCount - Failures that were revoked access (403/404).
   * @param failedCount - Total failures. Zero when the archive itself died
   * without any per-document failure, which is not an access problem.
   * @returns The toast copy for this shape of failure.
   */
  private _resolveAllFailedToast(
    submission: OutcomeReadySubmission,
    revokedCount: number,
    failedCount: number,
  ): string {
    if (failedCount > 0 && revokedCount === failedCount) {
      return OUTCOME_READY_TOAST.noLongerAvailable;
    }

    if (revokedCount > 0) {
      return OUTCOME_READY_TOAST.mixedFailure(
        revokedCount,
        submission.workspace_name,
      );
    }

    return OUTCOME_READY_TOAST.groupFailure(submission.workspace_name);
  }

  /** Raises the shared "we couldn't complete your download" toast. */
  private _showGeneralFailure(): void {
    this._showToast(
      OUTCOME_READY_TOAST.generalFailure,
      OUTCOME_READY_SNACKBAR_CLASS.error,
    );
  }

  /**
   * Marks the given documents as downloading.
   *
   * @param ids - Document ids whose downloads just started.
   */
  private _addDownloadingIds(ids: string[]): void {
    patchState(this._outcomeReadySignalState, (state) => ({
      downloadingIds: [
        ...state.downloadingIds,
        ...ids.filter((id) => !state.downloadingIds.includes(id)),
      ],
    }));
  }

  /**
   * Clears the downloading flag for the given documents.
   *
   * @param ids - Document ids whose downloads settled.
   */
  private _removeDownloadingIds(ids: string[]): void {
    patchState(this._outcomeReadySignalState, (state) => ({
      downloadingIds: state.downloadingIds.filter((id) => !ids.includes(id)),
    }));
    this._clearProgress(ids);
  }

  /**
   * Marks the given documents as owned by an in-flight zipped group download.
   *
   * @param ids - Document ids the archive will fetch.
   */
  private _addQueuedIds(ids: string[]): void {
    patchState(this._outcomeReadySignalState, (state) => ({
      queuedIds: [
        ...state.queuedIds,
        ...ids.filter((id) => !state.queuedIds.includes(id)),
      ],
    }));
  }

  /**
   * Releases documents from a settled zipped group download.
   *
   * @param ids - Document ids to release.
   */
  private _removeQueuedIds(ids: string[]): void {
    patchState(this._outcomeReadySignalState, (state) => ({
      queuedIds: state.queuedIds.filter((id) => !ids.includes(id)),
    }));
  }

  /**
   * Records a document's transfer percentage.
   *
   * A `null` percent means the server exposed no readable `Content-Length`; the
   * id is removed from the map so the row falls back to an indeterminate
   * indicator instead of appearing stuck at its last known value.
   *
   * @param id - Document id being transferred.
   * @param percent - Whole-percent progress, or null when the total is unknown.
   */
  private _setProgress(id: string, percent: number | null): void {
    patchState(this._outcomeReadySignalState, (state) => {
      const _next = { ...state.downloadProgress };

      if (percent === null) {
        delete _next[id];
      } else {
        _next[id] = percent;
      }

      return { downloadProgress: _next };
    });
  }

  /**
   * Drops progress entries for documents whose downloads have settled.
   *
   * @param ids - Document ids to clear.
   */
  private _clearProgress(ids: string[]): void {
    patchState(this._outcomeReadySignalState, (state) => {
      const _next = { ...state.downloadProgress };
      for (const _id of ids) {
        delete _next[_id];
      }
      return { downloadProgress: _next };
    });
  }

  /**
   * Sets a submission group's header phase, auto-clearing terminal phases.
   *
   * `downloading` persists until the group settles. `success`, `partial`, and
   * `error` are transient and clear themselves after
   * {@link OUTCOME_READY_GROUP_PHASE_RESET_MS}, mirroring the pinned-items
   * `saved` indicator. Re-downloading a group restarts its dwell rather than
   * inheriting the previous timer.
   *
   * @param workspaceId - Workspace id of the group to update.
   * @param phase - Phase to apply to that group's header.
   */
  private _setGroupPhase(workspaceId: string, phase: OutcomeGroupPhase): void {
    this._clearPhaseTimer(workspaceId);

    patchState(this._outcomeReadySignalState, (state) => ({
      groupPhases: { ...state.groupPhases, [workspaceId]: phase },
    }));

    if (phase === 'downloading') {
      return;
    }

    const _timer = setTimeout(() => {
      this._phaseTimers.delete(workspaceId);
      patchState(this._outcomeReadySignalState, (state) => {
        const _next = { ...state.groupPhases };
        delete _next[workspaceId];
        return { groupPhases: _next };
      });
    }, OUTCOME_READY_GROUP_PHASE_RESET_MS);

    this._phaseTimers.set(workspaceId, _timer);
  }

  /**
   * Cancels a single group's pending phase-reset timer, if one is scheduled.
   *
   * @param workspaceId - Workspace id whose timer should be cancelled.
   */
  private _clearPhaseTimer(workspaceId: string): void {
    const _timer = this._phaseTimers.get(workspaceId);
    if (_timer !== undefined) {
      clearTimeout(_timer);
      this._phaseTimers.delete(workspaceId);
    }
  }

  /** Cancels every pending phase-reset timer. */
  private _clearAllPhaseTimers(): void {
    for (const _timer of this._phaseTimers.values()) {
      clearTimeout(_timer);
    }
    this._phaseTimers.clear();
  }
}
